import Axios from 'axios'
import { enqueueSnackbar } from 'notistack'
import config from './config'
import { addChildClientHeader } from './client-scope'
import { getRateLimitMessage } from '/utils/rate-limit-message'

const authApiUrls = {
  remote: config.authRemoteApiUrl,
  local: config.authLocalApiUrl
}

let runtime = {
  applyLoginResponse: null,
  handleAuthenticationFailure: null,
}

let refreshPromise = null

const configureAuthenticatedApi = handlers => {
  runtime = {
    ...runtime,
    ...handlers,
  }
}

const callAuthenticatedApi = async ({ apiSource, apiKey, options, includeChildClientId = true, apiMode }) => {
  const execute = async key => {
    const headers = {
      'x-api-key': key,
      'Content-type': 'application/json',
      ...(apiMode !== undefined ? { 'x-mode': apiMode } : {})
    }

    options.headers = includeChildClientId ? addChildClientHeader(headers) : headers
    options.withCredentials = true

    const response = await Axios(options)
    return response.data
  }

  const executeWithRateLimitHandling = async key => {
    try {
      return await execute(key)
    }
    catch (err) {
      if (err?.response?.status === 429) return normaliseRateLimitResponse(err)
      throw err
    }
  }

  try {
    return await executeWithRateLimitHandling(apiKey)
  }
  catch (err) {
    if (!isAuthenticationError(err)) throw err

    const refreshedKey = await refreshLoginKey(apiSource)
    if (!refreshedKey) throw err

    return await executeWithRateLimitHandling(refreshedKey)
  }
}

export {
  callAuthenticatedApi,
  configureAuthenticatedApi,
}

const isAuthenticationError = err => {
  const error = err?.response?.data?.error

  return error?.error_code === 'AUTHENTICATION_FAILED'
    || error?.error_type === 'authentication'
    || error?.type === 'AuthenticationError'
}

const normaliseRateLimitResponse = err => {
  const responseData = err.response?.data ?? {}
  const sourceError = responseData.error
  const error = sourceError && typeof sourceError === 'object'
    ? sourceError
    : { message: typeof sourceError === 'string' ? sourceError : 'Too many requests. Please try again later.' }
  const retryAfterSeconds = getRetryAfterSeconds(err.response?.headers)
  const normalisedError = {
    ...error,
    status_code: 429,
    ...(retryAfterSeconds == null ? {} : { retry_after_seconds: retryAfterSeconds })
  }

  enqueueSnackbar({ message: getRateLimitMessage(normalisedError), variant: 'warning' })

  return {
    ...responseData,
    ok: false,
    error: normalisedError
  }
}

const getRetryAfterSeconds = headers => {
  const retryAfter = headers?.['retry-after'] ?? headers?.get?.('retry-after')
  if (retryAfter == null) return null

  const seconds = Number(retryAfter)
  if (Number.isFinite(seconds) && seconds >= 0) return Math.ceil(seconds)

  const retryDate = Date.parse(retryAfter)
  if (Number.isNaN(retryDate)) return null

  return Math.max(0, Math.ceil((retryDate - Date.now()) / 1000))
}

const refreshLoginKey = async apiSource => {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const response = await Axios({
          method: 'GET',
          url: `${authApiUrls[apiSource]}/checkLoginToken`,
          withCredentials: true
        })

        const loginResponse = response.data

        if (loginResponse?.authenticated && loginResponse?.key) {
          await runtime.applyLoginResponse?.(loginResponse, { preserveSelectedClient: true })
          return loginResponse.key
        }

        await runtime.handleAuthenticationFailure?.()
        return null
      }
      catch (err) {
        await runtime.handleAuthenticationFailure?.()
        return null
      }
      finally {
        refreshPromise = null
      }
    })()
  }

  return await refreshPromise
}
